import { API_URL, config, Environment } from "@/config";
import { CRYPTO_SECRET_KEY } from "../constants";
import CryptoJS from "crypto-js";
import $axios from "@/core/utils/axios-api-config";
import {
  AgreeTermsConsumerDisclosure,
  DurationsType,
  ContractorInvitePermissionEnum,
  EstimatePhaseStatus,
} from "../enums/estimateEnum";
import moment from "moment";
import { SafetyStandardStatusEnum } from "../enums/PropertiesEnum";

export const imageExtensions = [
  "bmp",
  "rle",
  "dib",
  "gif",
  "jpeg",
  "jpg",
  "jpe",
  "exif",
  "png",
  "tiff",
  "tif",
  "icon",
  "ico",
  "mwf",
  "emf",
  "svg",
];

export const documentExtensions = [
  "pptx",
  "pdf",
  "doc",
  "docx",
  "text",
  "txt",
  "mov",
  "mp4",
  "mpg",
  "mpeg",
  "heic",
  "zip",
  "rar",
  "tar",
  "gzip",
  "mp3",
  "waw",
  "wav",
  "ogg",
  "opus",
  "hevc",
  "webm",
  "mpeg4",
  "3gpp",
  "3gp",
  "gz",
  "avi",
  "mpegps",
  "wmv",
  "flv",
];

export const cloneObject = (value: any) => {
  return JSON.parse(JSON.stringify(value));
};

export const setLocalStorageWithExpiry = (
  value: any,
  key: string,
  ttl: number
) => {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value,
    expiry: now.getTime() + ttl,
  };
  window.localStorage.setItem(key, JSON.stringify(item));
};

export const parseJSON = (data: any) => {
  try {
    if (typeof data === "string") {
      return JSON.parse(data);
    } else {
      return data;
    }
  } catch (error) {
    return null;
  }
};

export const getLocalStorageWithExpiry = (key: string) => {
  const itemStr = window.localStorage.getItem(key);
  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }
  const item = parseJSON(itemStr);
  if (!item) {
    return null;
  }
  const now = new Date();
  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    //   // If the item is expired, delete the item from storage
    // and return null
    window.localStorage.removeItem(key);
    return null;
  }
  return item.value;
};

export const removeLocalStorageByKey = (key: string) => {
  window.localStorage.removeItem(key);
};

export const deleteAllCookies = () => {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
};

export const getImageApiUrl = (profileImage: string, encoded = false) => {
  let encodedParam = "";
  const encryptedToken = CryptoJS.AES.encrypt("", CRYPTO_SECRET_KEY).toString();

  if (encoded) {
    encodedParam = "encoded=1&";
  }
  const url = `${API_URL}upload?${encodedParam}key=${profileImage}&token=${encodeURIComponent(
    encryptedToken
  )}`;
  return url;
};
export const profileImagesData: any = {};
export const getImageStringToImageURL = async (profileImage: string) => {
  try {
    if (!profileImage) return null;

    if (profileImage in profileImagesData) {
      return profileImagesData[profileImage];
    }

    const imageUrl = setDirectStoragePathToImage(profileImage);
    profileImagesData[profileImage] = imageUrl;
    return imageUrl;
  } catch (error) {
    return null;
  }
};

export const setDirectStoragePathToImage = (
  relativeUrl: string | null
): string | null => {
  let baseUrl = "";
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  const subdomain = parts.length > 2 ? parts[1] : null;

  if (subdomain && subdomain.includes(config.staging.hostname)) {
    baseUrl = "https://storage.googleapis.com/isgc-b10/";
  } else if (subdomain && subdomain.includes(config.production.hostname)) {
    baseUrl = "https://storage.googleapis.com/ipgc-b10/";
  } else if (subdomain && subdomain.includes(config.development.hostname)) {
    baseUrl = "https://storage.googleapis.com/idgc-b10/";
  } else {
    baseUrl = "https://storage.googleapis.com/idgc-b10/";
  }

  return relativeUrl ? `${baseUrl}${relativeUrl}` : null;
};

export const getEnvironment = (): Environment => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const subdomain = parts.length > 2 ? parts[1] : hostname;

  if (hostname === "localhost") {
    return Environment.LOCAL;
  }

  if (subdomain.includes(config[Environment.PRODUCTION].hostname)) {
    return Environment.PRODUCTION;
  } else if (subdomain.includes(config[Environment.STAGING].hostname)) {
    return Environment.STAGING;
  } else if (subdomain.includes(config[Environment.DEVELOPMENT].hostname)) {
    return Environment.DEVELOPMENT;
  }

  return Environment.LOCAL;
};

export const isExpiredDate = (dateString: string) => {
  const currentDate = new Date();
  currentDate.setHours(0, 0, 0, 0);
  const inputDate = new Date(dateString);
  inputDate.setHours(0, 0, 0, 0);
  return inputDate < currentDate;
};

export const isAliased = (value: string): boolean => {
  return value.indexOf("iknowa.alias.") > -1;
};

export const getRandomColor = () => {
  const upperLimit = 200;
  const red = Math.floor(Math.random() * upperLimit);
  const green = Math.floor(Math.random() * upperLimit);
  const blue = Math.floor(Math.random() * upperLimit);
  const hexColor = `#${red.toString(16).padStart(2, "0")}${green
    .toString(16)
    .padStart(2, "0")}${blue.toString(16).padStart(2, "0")}`;
  return hexColor;
};

export const getTotalDuration = (
  startDate: string,
  endDate: string,
  durationType: number
) => {
  if (!endDate) {
    return null;
  }
  const startedDate: any = new Date(startDate);
  const endedDate: any = new Date(endDate);

  const timeDifference = endedDate - startedDate;

  let duration;
  if (durationType === DurationsType.DAY) {
    duration = timeDifference / (1000 * 60 * 60 * 24);
  } else {
    duration = timeDifference / (1000 * 60 * 60 * 24 * 7);
  }

  return duration;
};

export const filterSubContracts = (initialId: number, contracts: any) => {
  const result: any = [];

  function findContracts(currentId: number) {
    for (const contract of contracts) {
      if (contract.fromContractorId === currentId) {
        result.push(contract);
        findContracts(contract.toContractorId);
      }
    }
  }

  findContracts(initialId);

  return result;
};

export const getCurrentConTractorPhases = (initialId: any, phases: any) => {
  return phases.filter((el: any) => el.workStation?.id == initialId);
};

export const getSubContractorPhases = (
  initialId: any,
  contractors: any,
  phases: any
) => {
  const subContractors = [] as any;

  //invite contrcators
  const getSubContractors = (currID: any) => {
    contractors.map((con: any) => {
      if (con.fromContractorId == currID) {
        subContractors.push(con);
        getSubContractors(con.toContractorId);
      }
    });
  };

  getSubContractors(initialId);

  const subContractorphases = [] as any;

  const getSubContractorPhases = (currID: any) => {
    const arr = subContractors.filter(
      (el: any) => el.fromContractorId == currID
    );

    arr.map((ele: any) => {
      //phase pending
      if (
        ele.agreeTermsConsumerDisclosure == AgreeTermsConsumerDisclosure.PENDING
      ) {
        phases.map((el: any) => {
          if (el.workStation.id == ele.toContractorId) {
            subContractorphases.push(el);
          }
        });
        getSubContractorPhases(ele.toContractorId);
      }
    });
  };
  getSubContractorPhases(initialId);

  return subContractorphases.filter(
    (el: any) => el.phaseStatus !== EstimatePhaseStatus.REJECTED
  );
};

export const getParentContractorPhases = (
  initialId: any,
  contractors: any,
  phases: any
) => {
  try {
    const parentContractors = [] as any;

    //invite contractor toWorkstation Id
    const getParentContractors = (currID: any) => {
      contractors.map((con: any) => {
        if (con.toContractorId == currID) {
          parentContractors.push(con);
          getParentContractors(con.fromContractorId);
        }
      });
    };

    getParentContractors(initialId);
    const parentContractorPhases = {} as any;

    const getParentContractorPhases = (currID: any) => {
      const parent = parentContractors.find(
        (el: any) => el.toContractorId == currID
      );
      if (parent) {
        // permission check subcontractor and empty task init
        phases.map((pha: any) => {
          if (pha.workStation.id == parent.fromContractorId) {
            if (!parentContractorPhases[parent.fromContractorId]) {
              parentContractorPhases[parent.fromContractorId] = {
                tasks: [],
                permission: parent.permission_type,
              };
            }
            parentContractorPhases[parent.fromContractorId].tasks.push(pha);
          }
        });
        const getData = (newCurrID: any) => {
          const subsOfParent = contractors.filter(
            (ele: any) =>
              ele.fromContractorId == newCurrID && ele.toContractorId !== currID
          );
          subsOfParent.map((ele: any) => {
            if (
              ele.agreeTermsConsumerDisclosure ==
              AgreeTermsConsumerDisclosure.PENDING
            ) {
              phases.map((el: any) => {
                if (el.workStation.id == ele.toContractorId) {
                  if (!parentContractorPhases[parent.fromContractorId]) {
                    parentContractorPhases[parent.fromContractorId] = {
                      tasks: [],
                      permission: parent.permission_type,
                    };
                  }
                  parentContractorPhases[parent.fromContractorId].tasks.push(
                    el
                  );
                  getData(ele.toContractorId);
                }
              });
            }
          });
          getParentContractorPhases(newCurrID);
        };
        //parent workstationId
        getData(parent.fromContractorId);
      }
    };

    getParentContractorPhases(initialId);

    let finalPhases = [] as any;

    Object.keys(parentContractorPhases).map((parentID: any) => {
      if (
        parentContractorPhases[parentID].permission ==
        ContractorInvitePermissionEnum.ALLPHASE
      ) {
        //all subcontractor tree
        parentContractorPhases[parentID].tasks.map((ele: any) => {
          finalPhases.push(ele);
        });
      }

      if (
        parentContractorPhases[parentID].permission ==
        ContractorInvitePermissionEnum.BYMAINCONTRACTORONLY
      ) {
        //only parentPhases tasks
        const tempfinal = [
          ...finalPhases,
          ...parentContractorPhases[parentID].tasks,
        ];
        const finalData = tempfinal.filter(
          (ele: any) => ele.workStation.id == parentID
        );
        finalPhases = finalData;
      }
    });
    return finalPhases;
  } catch (error) {
    console.log("error", error);
  }
};

export const panelsPalette = ["E8EAF6", "1A237E"];
export function lerp(x: number, y: number, t: number): number {
  return x + t * (y - x);
}
export function colorToRGB(color: string): { r: number; g: number; b: number } {
  const hex = color.startsWith("#") ? color.slice(1) : color;
  return {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
  };
}
export function createPalette(
  hexColors: string[]
): { r: number; g: number; b: number }[] {
  // Map each hex color into an RGB value.
  const rgb = hexColors.map(colorToRGB);
  // Create a palette with 256 colors derived from our rgb colors.
  const size = 256;
  const step = (rgb.length - 1) / (size - 1);
  return Array(size)
    .fill(0)
    .map((_, i) => {
      // Get the lower and upper indices for each color.
      const index = i * step;
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      // Interpolate between the colors to get the shades.
      return {
        r: lerp(rgb[lower].r, rgb[upper].r, index - lower),
        g: lerp(rgb[lower].g, rgb[upper].g, index - lower),
        b: lerp(rgb[lower].b, rgb[upper].b, index - lower),
      };
    });
}
export function normalize(x: any, max = 1, min = 0) {
  const y = (x - min) / (max - min);
  return clamp(y, 0, 1);
}
export function clamp(x: number, min: number, max: number) {
  return Math.min(Math.max(x, min), max);
}
export function rgbToColor({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): string {
  const f = (x: number) => {
    const hex = Math.round(x).toString(16);
    return hex.length == 1 ? `0${hex}` : hex;
  };
  return `#${f(r)}${f(g)}${f(b)}`;
}
export const getUserSubSkillList = (userSkill: any, getAllSpecialism: any) => {
  try {
    const userSkillClone = JSON.parse(JSON.stringify(userSkill));
    let skillSubCategoryList: any = [];
    userSkillClone.forEach((skill: any) => {
      const subCategoryList: any = getAllSpecialism.find(
        (specialism: any) => specialism.id === skill.category.id
      );
      const skillSubCategory = subCategoryList.subCategories.filter(
        (category: any) => parseJSON(skill.subCategoryIds).includes(category.id)
      );
      skillSubCategoryList = [
        ...skillSubCategory.map((category: any) => ({
          ...category,
          iconName: skill.category.iconName,
        })),
        ...skillSubCategoryList,
      ];
    });
    return skillSubCategoryList;
  } catch (error) {
    return [];
  }
};

export const getStatusBasedOnExpiryDateOfSafetyStandard = (
  expiryDate: string
) => {
  const now = moment();
  const expiry = moment(expiryDate);
  const differenceInDays = expiry.diff(now, "days");
  if (differenceInDays < 0) {
    return { label: SafetyStandardStatusEnum.EXPIRED, color: "#ff3636" };
  } else if (differenceInDays <= 7) {
    return { label: SafetyStandardStatusEnum.ATTENTION, color: "#036700" };
  } else if (differenceInDays <= 90) {
    return { label: SafetyStandardStatusEnum.EXPIRED_SOON, color: "#9dcb3c" };
  } else {
    return { label: SafetyStandardStatusEnum.VALID, color: "#2c9f29" };
  }
};

export const displayToastMessage = (
  internalInstance: any,
  message: string,
  type: string
) => {
  if (
    internalInstance &&
    internalInstance.appContext.config.globalProperties.$notify
  ) {
    internalInstance.appContext.config.globalProperties.$notify[type]({
      message,
    });
  }
};

const averageRating = (ratingList: any) => {
  if (ratingList.length === 0) return [];

  const totalRating = ratingList.reduce(
    (sum: any, { rating }: any) => sum + rating,
    0
  );
  const avgRating = totalRating / ratingList.length;
  return avgRating ? Math.round(avgRating * 100) / 100 : 0;
};
const averageRatingsFromNestedArrays = (nestedRatings: any) => {
  const ratingSum = nestedRatings.reduce((acc: any, ratingList: any) => {
    ratingList.forEach(({ tag, rating }: any) => {
      if (!acc[tag]) {
        acc[tag] = { total: 0, count: 0 };
      }
      acc[tag].total += rating;
      acc[tag].count += 1;
    });
    return acc;
  }, {});

  return Object.keys(ratingSum).map((tag) => ({
    tag: tag,
    rating: Math.round(ratingSum[tag].total / ratingSum[tag].count),
  }));
};
export const getCombineSameProjectReview = (getAllProjectReviewList: any) => {
  const groupByProjectId = getAllProjectReviewList.reduce(
    (acc: any, review: any) => {
      if (!parseJSON(review.ratingStatusList)?.length) return acc;
      if (!acc[review.project.id]) {
        acc[review.project.id] = {
          projectData: review,
          allReviewList: [],
          avgRatingStatusList: review.project.projectPhaseRating.map(
            (item: any) => parseJSON(item.ratingStatusList)
          ),
          comments: review.project.projectPhaseRating.map((item: any) => ({
            user: item.user,
            description: item.description,
            createDate: item.dateCreated,
          })),
        };
      }
      acc[review.project.id].allReviewList = [
        ...acc[review.project.id].allReviewList,
        review,
        ...review.project.projectPhaseRating,
      ];

      acc[review.project.id].avgRatingStatusList = [
        ...acc[review.project.id].avgRatingStatusList,
        parseJSON(review.ratingStatusList),
      ];
      acc[review.project.id].comments.push({
        user: review.user,
        description: review.description,
        createDate: review.dateCreated,
      });
      return acc;
    },
    {}
  );
  const finalProjectReviewList = Object.values(groupByProjectId).map(
    (project: any) => {
      const avgRatingStatusList = averageRatingsFromNestedArrays(
        project.avgRatingStatusList
      );
      return {
        project: project.projectData.project,
        type: project.projectData.type,
        totalAvg: averageRating(avgRatingStatusList),
        avgRatingStatusList,
        comments: project.comments,
      };
    }
  );
  return finalProjectReviewList;
};
export const isValidDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};

export const formatFullDurationForVideoConsultation = (
  estimationPhase: any
) => {
  let ms = 0;
  estimationPhase?.forEach((phase: any) => {
    ms += +phase?.VideoConsultationHoursUsed;
  });
  if (ms <= 0) {
    return 0;
  }
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / (1000 * 60)) % 60;
  const hours = Math.floor(ms / (1000 * 60 * 60));

  const result = [];

  if (hours > 0) {
    result.push(`${hours} hour${hours > 1 ? "s" : ""}`);
  }
  if (minutes > 0) {
    result.push(`${minutes} minute${minutes > 1 ? "s" : ""}`);
  }
  if (seconds > 0) {
    result.push(`${seconds} second${seconds > 1 ? "s" : ""}`);
  }

  return result.join(" ");
};
export const generateUniqueId = (length = 32) => {
  const chars = "0123456789abcdefghijklmnopqrstuvwxyz";
  let uuid = "";

  for (let i = 0; i < length; i++) {
    if (i === 8 || i === 12 || i === 16 || i === 20) {
      uuid += "-";
    }
    uuid += chars[Math.floor(Math.random() * chars.length)];
  }

  return uuid;
};

export const generateLightColor = (): string => {
  const g = Math.floor(Math.random() * 128) + 128;
  const b = Math.floor(Math.random() * 128) + 128;
  const r = Math.floor(Math.random() * 128) + 128;

  return rgbToHex({ r, g, b });
};

const rgbToHex = (color: { r: number; g: number; b: number }): string => {
  return `#${((1 << 24) | (color.r << 16) | (color.g << 8) | color.b)
    .toString(16)
    .slice(1)
    .padStart(6, "0")
    .toUpperCase()}`;
};

type DebounceFunction = (...args: any[]) => void;

export const customDebounce = (
  func: DebounceFunction,
  delay: number
): DebounceFunction => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export const formateDate = (date: Date, format = "DD MMM YYYY") => {
  try {
    const formattedDate = moment(date).format(format);
    return formattedDate;
  } catch (error) {
    return null;
  }
};

export const getTwoChartOfLogo = (fullName: string) => {
  if (fullName !== "" && fullName !== null && fullName !== undefined) {
    const nameParts = fullName.split(" ").filter((part) => part !== "");

    if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    } else if (nameParts.length >= 2) {
      const firstInitial = nameParts[0][0].toUpperCase();
      const lastInitial = nameParts[nameParts.length - 1][0].toUpperCase();
      return firstInitial + lastInitial;
    }
  }

  return "IK";
};
